import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, ImagePlus, Trash2 } from 'lucide-react';
import api from '../lib/api';
import { Button } from './ui/button';

export default function GroupLogoUploader({ group, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [logoUrl, setLogoUrl] = useState(group?.logo_url || null);
  const fileRef = useRef(null);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`/groups/${group.id}/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setLogoUrl(`${r.data.logo_url}?v=${Date.now()}`);
      toast.success('Group logo updated');
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/groups/${group.id}/logo`);
      setLogoUrl(null);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove logo');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border p-3 flex items-center gap-3" data-testid="group-logo-row">
      <div className="w-14 h-14 rounded-full border-2 border-primary/30 bg-muted overflow-hidden flex items-center justify-center shrink-0">
        {logoUrl ? (
          <img src={`${process.env.REACT_APP_BACKEND_URL}${logoUrl}`} alt="Group logo" className="w-full h-full object-cover" data-testid="group-logo-preview" />
        ) : (
          <span className="text-2xl">{group?.emoji || '🏷️'}</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold">Group logo</p>
        <p className="text-xs text-muted-foreground">Shows on the group header, dashboard, ledger PDFs and Play cards. Square PNG/JPG works best.</p>
      </div>
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={upload} data-testid="group-logo-input" />
      <div className="flex flex-col gap-1">
        <Button type="button" size="sm" variant="outline" className="rounded-full h-8 text-xs" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="group-logo-upload-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><ImagePlus className="w-3.5 h-3.5 mr-1" /> {logoUrl ? 'Replace' : 'Upload'}</>}
        </Button>
        {logoUrl && (
          <Button type="button" size="sm" variant="ghost" className="rounded-full h-7 text-xs text-rose-600" disabled={busy} onClick={remove} data-testid="group-logo-remove-btn">
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
          </Button>
        )}
      </div>
    </div>
  );
}
